import { useState, useEffect, useCallback } from 'react';
import { Milestone, Filter, X } from 'lucide-react';
import Select from 'react-select';
import { salesApiInstance } from '../../api/client.js';
import { usersApi } from '../../api/users.js';

const inp = {
  padding: '7px 11px', border: '1px solid #d1d5db', borderRadius: 7,
  fontSize: '0.84rem', background: '#fff', outline: 'none',
  color: '#111827', boxSizing: 'border-box',
};
const thStyle = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const tdStyle = { padding: '11px 14px', fontSize: '0.85rem', verticalAlign: 'middle' };
const PAGE_LIMITS = [20, 40, 80];

function Pagination({ page, pages, limit, onLimitChange, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
      <button onClick={() => onChange(page - 1)} disabled={page <= 1} style={{ ...inp, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.4 : 1, padding: '5px 14px' }}>←</button>
      {pages > 0 && <span style={{ fontSize: '0.83rem', color: '#6b7280', padding: '0 4px' }}>Page {page} of {pages}</span>}
      <button onClick={() => onChange(page + 1)} disabled={page >= pages} style={{ ...inp, cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? 0.4 : 1, padding: '5px 14px' }}>→</button>
      <select value={limit} onChange={e => { onLimitChange(Number(e.target.value)); onChange(1); }} style={{ ...inp, marginLeft: 8, padding: '5px 8px', cursor: 'pointer' }}>
        {PAGE_LIMITS.map(l => <option key={l} value={l}>{l} / page</option>)}
      </select>
    </div>
  );
}

const FilterLabel = ({ children }) => (
  <span style={{ fontSize: '0.71rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>
    {children}
  </span>
);

function formatDate(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function DistanceHistoryPage() {
  const [salesPersons, setSalesPersons] = useState([]);
  const [salesId, setSalesId] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    usersApi.getAll({ role: 'sales', limit: 200 })
      .then((res) => {
        const list = res.data ?? res;
        const roster = Array.isArray(list) ? list : (list.data ?? []);
        setSalesPersons(roster.filter((u) => u.is_active !== false));
      })
      .catch(() => {});
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit };
      if (salesId) params.sales_id = salesId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await salesApiInstance.get('/shift/distance-history', { params });
      setRows(data?.rows ?? []);
      setPages(data?.pages ?? 0);
      setTotal(data?.total ?? 0);
    } catch (err) {
      console.error('[distance-history] fetch error:', err);
      setError(err?.response?.data?.error || 'Failed to load distance history.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, salesId, dateFrom, dateTo]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const salesPersonOptions = salesPersons.map((p) => ({ value: String(p.id), label: p.name }));
  const clearFilters = () => {
    setSalesId(null);
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };
  const hasFilters = !!(salesId || dateFrom || dateTo);

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
        <Milestone size={22} color="#2563eb" />
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111827' }}>
          Distance History
        </h2>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, color: '#374151', fontWeight: 600, fontSize: '0.85rem' }}>
          <Filter size={14} /> Filters
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ width: 220 }}>
            <FilterLabel>Sales Person</FilterLabel>
            <Select
              options={salesPersonOptions}
              value={salesPersonOptions.find((o) => o.value === salesId) || null}
              onChange={(option) => { setSalesId(option?.value || null); setPage(1); }}
              placeholder="All sales persons..."
              isClearable
            />
          </div>
          <div>
            <FilterLabel>From Date</FilterLabel>
            <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} style={inp} />
          </div>
          <div>
            <FilterLabel>To Date</FilterLabel>
            <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} style={inp} />
          </div>
          {hasFilters && (
            <button onClick={clearFilters} style={{ ...inp, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#374151' }}>
              <X size={13} /> Clear
            </button>
          )}
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Sales Person</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Total Distance</th>
                <th style={thStyle}>Total Visits Done</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td style={tdStyle} colSpan={4}>Loading…</td></tr>
              )}
              {!loading && error && (
                <tr><td style={{ ...tdStyle, color: '#dc2626' }} colSpan={4}>{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td style={{ ...tdStyle, color: '#6b7280' }} colSpan={4}>No recorded distance for the selected filters.</td></tr>
              )}
              {!loading && !error && rows.map((r, i) => (
                <tr key={`${r.salesPersonId}-${r.date}-${i}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{r.salesPersonName}</td>
                  <td style={tdStyle}>{formatDate(r.date)}</td>
                  <td style={tdStyle}>{r.totalDistanceKm} km</td>
                  <td style={tdStyle}>{r.totalVisits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '0 14px 14px' }}>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 10 }}>
            {total} record{total === 1 ? '' : 's'}
          </div>
          <Pagination page={page} pages={pages} limit={limit} onLimitChange={setLimit} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}
